import React, { useState } from 'react';
import { getTravelApiBaseUrl } from '../lib/apiConfig';
import { sessionToken, setSessionToken } from './p3Session';
import { guestTravelerId, bindStateToAccount } from './P3TestBench';

const SUPPORT_EMAIL = 'info@freshroad.com';
// Mirrors ACCOUNT_SMS_TEXT in backend/travel/p3Accounts.js (stored with each sign-up as proof of consent).
export const SMS_CONSENT = 'I agree to receive text messages from Freshroad Travel Pass, including login codes and trip notifications. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel.';

const css = `
.su{ --ink:#173e34; --green:#285d47; --muted:#6d7a71; --line:#e4e8de; --mint:#e4eedf; --peach:#f7e7d8;
  min-height:100vh; display:flex; background:#f7f8f2; color:var(--ink); font-family:"DM Sans",-apple-system,"Segoe UI","PingFang SC",sans-serif; }
.su *{ box-sizing:border-box; }
.su-side{ display:none; }
.su-body{ flex:1; min-width:0; display:flex; flex-direction:column; }
.su-top{ padding:20px 28px; }
.su-logo{ display:inline-flex; align-items:center; gap:10px; font-family:Manrope,"DM Sans",sans-serif; font-weight:800; font-size:18px; letter-spacing:-.02em; color:var(--ink); text-decoration:none; }
.su-mark{ width:34px; height:34px; border-radius:11px; background:var(--green); color:#f0dc91; display:grid; place-items:center; font-size:16px; }
.su-main{ flex:1; display:flex; justify-content:center; align-items:flex-start; padding:24px 16px 48px; }
.su-card{ width:100%; max-width:440px; background:#fff; border:1px solid var(--line); border-radius:24px; padding:36px 36px 30px; box-shadow:0 8px 30px rgba(23,62,52,.05); }
.su h1{ font-family:Manrope,"DM Sans",sans-serif; font-size:30px; line-height:1.15; font-weight:750; letter-spacing:-.02em; margin:0 0 6px; color:var(--ink); text-transform:none; }
.su-sub{ margin:0 0 26px; color:var(--muted); font-size:15.5px; line-height:1.5; }
.su-field{ margin-bottom:16px; }
.su-field label{ display:block; font-size:14px; font-weight:600; color:var(--ink); margin-bottom:6px; }
.su-field input{ width:100%; height:50px; font:inherit; font-size:16px; color:var(--ink); padding:0 16px; border:1px solid #d5ddd0; border-radius:14px; background:#fbfcf9; transition:border-color .15s, box-shadow .15s; }
.su-field input:focus{ outline:none; border-color:var(--green); background:#fff; box-shadow:0 0 0 3px rgba(40,93,71,.15); }
.su-hint{ margin-top:6px; font-size:13px; color:var(--muted); }
.su-pw{ position:relative; }
.su-pw input{ padding-right:68px; }
.su-pw button{ position:absolute; right:6px; top:50%; transform:translateY(-50%); border:0; background:none; font:inherit; font-size:13px; font-weight:600; color:var(--green); padding:8px 10px; cursor:pointer; text-transform:none; }
.su-check{ display:flex; gap:10px; align-items:flex-start; margin:14px 0 0; font-size:13.5px; line-height:1.5; color:var(--muted); cursor:pointer; }
.su-check input{ width:18px; height:18px; margin:1px 0 0; flex:0 0 18px; accent-color:var(--green); }
.su a{ color:var(--green); font-weight:600; }
.su-btn{ margin-top:24px; width:100%; height:52px; border:0; border-radius:16px; background:var(--green); color:#fff; font:inherit; font-size:16px; font-weight:600; cursor:pointer; transition:background .15s; text-transform:none; }
.su a.su-btn{ display:grid; place-items:center; color:#fff; text-decoration:none; }
.su-btn:hover:not(:disabled){ background:#1f4c3a; }
.su-btn:focus-visible,.su-link:focus-visible,.su a:focus-visible{ outline:2px solid var(--green); outline-offset:2px; }
.su-btn:disabled{ opacity:.45; cursor:not-allowed; }
.su-err{ margin-top:14px; padding:10px 14px; border-radius:14px; background:#fdecea; color:#b42318; font-size:14px; }
.su-ok{ width:56px; height:56px; border-radius:18px; background:var(--mint); color:var(--green); display:grid; place-items:center; font-size:26px; margin-bottom:18px; }
.su-done p{ color:var(--muted); font-size:15.5px; line-height:1.6; }
.su-aside{ margin:-6px 0 0; text-align:right; font-size:14px; }
.su-code{ letter-spacing:.4em; font-size:22px !important; text-align:center; }
.su-note{ margin-top:14px; padding:10px 14px; border-radius:14px; background:var(--mint); color:var(--ink); font-size:14px; }
.su-link{ border:0; background:none; padding:0; font:inherit; font-weight:600; color:var(--green); cursor:pointer; text-decoration:underline; text-transform:none; }
.su-switch{ margin:22px 0 0; text-align:center; font-size:14.5px; color:var(--muted); }
.su-foot{ display:flex; flex-wrap:wrap; gap:8px 20px; justify-content:center; padding:20px 16px 28px; font-size:13px; color:var(--muted); }
.su-foot a{ color:var(--muted); font-weight:400; text-decoration:none; }
.su-foot a:hover{ text-decoration:underline; }
@media (max-width:599px){
  .su-top{ padding:16px 16px 0; }
  .su-main{ padding:16px 16px 32px; }
  .su-card{ padding:26px 20px 22px; border-radius:22px; }
  .su h1{ font-size:26px; }
}
@media (min-width:1024px){
  .su-side{ position:relative; overflow:hidden; display:flex; flex-direction:column; justify-content:space-between; flex:0 0 min(46%, 620px); padding:40px 48px; background:var(--mint); border-inline-end:1px solid #d6e3d0; }
  .su-side::before,.su-side::after{ content:""; position:absolute; border-radius:50%; z-index:0; }
  .su-side::before{ width:420px; height:420px; right:-140px; bottom:-120px; background:#d3e4cb; }
  .su-side::after{ width:220px; height:220px; right:-70px; top:-70px; background:var(--peach); opacity:.8; }
  .su-side > *{ position:relative; z-index:1; }
  .su-side h2{ font-family:Manrope,"DM Sans",sans-serif; font-size:44px; line-height:1.08; font-weight:750; letter-spacing:-.03em; margin:0 0 14px; max-width:460px; color:var(--ink); }
  .su-side p{ margin:0; font-size:17px; line-height:1.55; color:var(--muted); max-width:420px; }
  .su-kicker{ font-size:12px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--muted); margin-bottom:14px; }
  .su-points{ list-style:none; margin:26px 0 0; padding:0; display:grid; gap:10px; }
  .su-points li{ display:flex; gap:10px; align-items:center; font-size:15.5px; font-weight:600; }
  .su-points li span{ display:grid; place-items:center; width:36px; height:36px; border-radius:12px; background:#fff; font-size:18px; }
  .su-body .su-top{ visibility:hidden; }
  .su-main{ align-items:center; padding-top:0; }
}
`;

const api = (path) => `${getTravelApiBaseUrl()}/api/p3/account/${path}`;

async function post(path, body, token = sessionToken()) {
  const res = await fetch(api(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.success) throw new Error(data?.message || 'Something went wrong. Please try again.');
  return data;
}

/** Signed in: keep the session, tie this browser's saved state to the account, and go on to the app or to email verification. */
function signedIn({ token, account }) {
  setSessionToken(token);
  bindStateToAccount(account.accountId);
  window.location.hash = account.emailVerified ? '#/p3' : '#/p3-verify';
}

const resetToken = () => new URLSearchParams(window.location.hash.split('?')[1] || '').get('token') || '';

const TITLES = {
  signup: ['Create your account', 'It only takes a minute.'],
  login: ['Log in', 'Welcome back.'],
  verify: ['Check your email', 'Enter the 6-digit code we sent you. It expires in 30 minutes.'],
  forgot: ['Reset your password', "Enter your account's email and we'll send you a link to set a new password."],
  reset: ['Choose a new password', 'At least 8 characters. You will be signed out on every device.'],
};

/**
 * Freshroad Travel Pass accounts: sign up (#/p3-signup, with the optional A2P texts opt-in), log in (#/p3-login,
 * with the emailed code when two-step sign-in is on), verify the email (#/p3-verify), forgot password (#/p3-forgot)
 * and the reset link from the email (#/p3-reset?token=). The app (#/p3) needs a signed-in, verified account.
 */
export default function P3Signup({ mode = 'signup' }) {
  const [form, setForm] = useState({ email: '', password: '', code: '', terms: false });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [challenge, setChallenge] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = (fn) => async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    setNote('');
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = run(async () => {
    if (mode === 'signup') {
      signedIn(await post('register', { ...form, code: undefined, pageUrl: window.location.href, guestId: guestTravelerId() }, ''));
    } else if (mode === 'login' && challenge) {
      signedIn(await post('login/code', { challenge, code: form.code, guestId: guestTravelerId() }, ''));
    } else if (mode === 'login') {
      const data = await post('login', { email: form.email, password: form.password, guestId: guestTravelerId() }, '');
      if (data.mfaRequired) {
        setChallenge(data.challenge);
        setNote(`We emailed a login code to ${form.email.trim()}.`);
      } else signedIn(data);
    } else if (mode === 'verify') {
      await post('verify-email', { code: form.code });
      window.location.hash = '#/p3';
    } else if (mode === 'forgot') {
      setNote((await post('forgot-password', { email: form.email }, '')).message);
      setDone(true);
    } else if (mode === 'reset') {
      await post('reset-password', { token: resetToken(), password: form.password }, '');
      setSessionToken('');
      setDone(true);
    }
  });
  const resend = run(async () => {
    await post('resend-verification', {});
    setNote('We sent a new code.');
  });
  const logOut = run(async () => {
    await post('logout', {}).catch(() => {});
    setSessionToken('');
    window.location.hash = '#/p3-login';
  });

  const codeOk = /^\d{6}$/.test(form.code.trim());
  const ready = {
    signup: form.email.trim() && form.password.length >= 8 && form.terms,
    login: challenge ? codeOk : form.email.trim() && form.password,
    verify: codeOk,
    forgot: form.email.trim(),
    reset: form.password.length >= 8 && resetToken(),
  }[mode];
  const [title, sub] = mode === 'login' && challenge ? ['Enter your login code', 'Check your email for a 6-digit code. It expires in 30 minutes.'] : TITLES[mode];
  const button = { signup: 'Create account', login: challenge ? 'Log in' : 'Log in', verify: 'Verify email', forgot: 'Send reset link', reset: 'Set new password' }[mode];

  const emailField = (
    <div className="su-field">
      <label htmlFor="su-email">Email</label>
      <input id="su-email" type="email" autoComplete="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required dir="ltr" />
    </div>
  );
  const passwordField = (autoComplete, label = 'Password') => (
    <div className="su-field">
      <label htmlFor="su-pw">{label}</label>
      <div className="su-pw">
        <input id="su-pw" type={showPw ? 'text' : 'password'} autoComplete={autoComplete} value={form.password} onChange={set('password')} required minLength={autoComplete === 'new-password' ? 8 : undefined} aria-describedby={autoComplete === 'new-password' ? 'su-pw-hint' : undefined} />
        <button type="button" onClick={() => setShowPw((v) => !v)} aria-pressed={showPw}>{showPw ? 'Hide' : 'Show'}</button>
      </div>
      {autoComplete === 'new-password' && <div className="su-hint" id="su-pw-hint">At least 8 characters.</div>}
    </div>
  );
  const codeField = (
    <div className="su-field">
      <label htmlFor="su-code">6-digit code</label>
      <input id="su-code" className="su-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={form.code} onChange={set('code')} required dir="ltr" />
    </div>
  );

  return (
    <div className="su">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap" />
      <style>{css}</style>
      <aside className="su-side">
        <a className="su-logo" href="#/p3"><span className="su-mark" aria-hidden="true">✦</span>Freshroad Travel Pass</a>
        <div>
          <div className="su-kicker">Meet Milo</div>
          <h2>Your travel companion, from the first step.</h2>
          <p>Get connected, find your way and get things done abroad, with Milo by your side.</p>
          <ul className="su-points">
            <li><span aria-hidden="true">📶</span>Travel data that just works</li>
            <li><span aria-hidden="true">📞</span>A local number and calls in your language</li>
            <li><span aria-hidden="true">🧭</span>Help with the next small step</li>
          </ul>
        </div>
        <span />
      </aside>
      <div className="su-body">
        <header className="su-top">
          <a className="su-logo" href="#/p3"><span className="su-mark" aria-hidden="true">✦</span>Freshroad Travel Pass</a>
        </header>

        <main className="su-main">
          <div className="su-card">
            {done && mode === 'reset' ? (
              <div className="su-done">
                <div className="su-ok" aria-hidden="true">✓</div>
                <h1>Password changed</h1>
                <p>Log in with your new password.</p>
                <a className="su-btn" href="#/p3-login">Log in</a>
              </div>
            ) : done && mode === 'forgot' ? (
              <div className="su-done">
                <div className="su-ok" aria-hidden="true">✓</div>
                <h1>Check your email</h1>
                <p>{note} The link expires in 1 hour.</p>
                <a className="su-btn" href="#/p3-login">Back to log in</a>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <h1>{title}</h1>
                <p className="su-sub">{sub}</p>

                {mode === 'signup' && (
                  <>
                    {emailField}
                    {passwordField('new-password')}
                    <label className="su-check">
                      <input type="checkbox" checked={form.terms} onChange={set('terms')} required />
                      <span>I agree to the <a href="#/site/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="#/site/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>
                    </label>
                  </>
                )}
                {mode === 'login' && !challenge && (
                  <>
                    {emailField}
                    {passwordField('current-password')}
                    <p className="su-aside"><a href="#/p3-forgot">Forgot password?</a></p>
                  </>
                )}
                {((mode === 'login' && challenge) || mode === 'verify') && codeField}
                {mode === 'forgot' && emailField}
                {mode === 'reset' && (resetToken() ? passwordField('new-password', 'New password') : <div className="su-err" role="alert">This link is missing its code. Open the link from the email again, or ask for a new one.</div>)}

                <button className="su-btn" type="submit" disabled={busy || !ready}>{busy ? 'Please wait…' : button}</button>
                {note && <div className="su-note" role="status">{note}</div>}
                {error && <div className="su-err" role="alert">{error}</div>}

                {mode === 'signup' && <p className="su-switch">Already have an account? <a href="#/p3-login">Log in</a></p>}
                {mode === 'login' && <p className="su-switch">New here? <a href="#/p3-signup">Create an account</a></p>}
                {mode === 'verify' && (
                  <p className="su-switch">
                    No email? <button type="button" className="su-link" onClick={resend} disabled={busy}>Send a new code</button> · <button type="button" className="su-link" onClick={logOut} disabled={busy}>Use another account</button>
                  </p>
                )}
                {(mode === 'forgot' || mode === 'reset') && <p className="su-switch"><a href="#/p3-login">Back to log in</a></p>}
              </form>
            )}
          </div>
        </main>

        <footer className="su-foot">
          <span>© {new Date().getFullYear()} Fresh Road Inc.</span>
          <a href="#/site/terms">Terms</a>
          <a href="#/site/privacy">Privacy</a>
          <a href={`mailto:${SUPPORT_EMAIL}`}>Help</a>
        </footer>
      </div>
    </div>
  );
}
